// Mobile-as-webapp Router variant. Reuses native Router structure but mounts
// the web onboarding/settings routes from @web/modules/router/components/MainRoutes
// so a fresh-storage browser session can run the create/import wallet flow that
// the native build skips (native ships with a pre-existing keystore).
import React, { lazy, Suspense, useContext, useEffect, useRef } from 'react'
import { StyleSheet, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { Navigate, Route, Routes } from 'react-router-dom'

import Spinner from '@common/components/Spinner'

import { ControllersStateLoadedContext } from '@common/contexts/controllersStateLoadedContext'
import useController from '@common/hooks/useController'
import useRoute from '@common/hooks/useRoute'
import useTheme from '@common/hooks/useTheme'
import { AUTH_STATUS } from '@common/modules/auth/constants/authStatus'
import useAuth from '@common/modules/auth/hooks/useAuth'
import AuthenticatedRoute from '@common/modules/router/components/AuthenticatedRoute'
import KeystoreUnlockedRoute from '@common/modules/router/components/KeystoreUnlockedRoute'
import { ROUTES } from '@common/modules/router/constants/common'
import { isDev } from '@common/config/env'
import TrackProgressGalleryScreen from '@mobile/modules/dev/screens/TrackProgressGalleryScreen'
import { getInitialRoute } from '@common/modules/router/helpers'
import usePendingDeepLinkRestore from '@mobile/modules/router/usePendingDeepLinkRestore'
import flexbox from '@common/styles/utils/flexbox'
import { dismissBootLoader } from '@common/utils/bootLoader'
import DashboardScreen from '@mobile/modules/dashboard/screens/DashboardScreen'
import MobileTabBar from '@mobile/modules/dashboard/screens/home/components/MobileTabBar'
import KeyStoreUnlockScreen from '@mobile/modules/keystore/screens/KeyStoreUnlockScreen'
import SignAccountOpOverlay from '@mobile/modules/sign-account-op/components/SignAccountOpOverlay'
import SignMessageOverlay from '@mobile/modules/sign-message/components/SignMessageOverlay'
import WalletConnectProposalOverlay from '@mobile/modules/dashboard/screens/home/components/WalletConnectProposalOverlay'
import { SignAccountOpControllerStateProvider } from '@web/contexts/signAccountOpControllerStateContext'
import { FEATURES } from '@web/constants/features'

// Code-split the non-first-paint routes so the initial bundle only carries the
// unlock + dashboard path. Shield/Unshield drag in the heavy Railgun SDK and
// WebMainRoutes pulls the entire @web module tree (swap-and-bridge, settings);
// keeping them eager bloated the main entry. webpack emits each as its own
// chunk, fetched on first navigation to that route.
// Import factories kept as named consts so each chunk can be BOTH lazy-mounted
// and prefetched ahead of navigation. Without prefetch, the first click on a
// sidebar/tab destination suspends on a cold chunk fetch and `Suspense
// fallback={null}` blanks the whole subtree (incl. the sidebar) for a few
// hundred ms — the visible "flash". Prefetching after first paint warms every
// chunk so subsequent navigations mount synchronously (no Suspense fallback).
const importActivity = () => import('@mobile/modules/dashboard/screens/ActivityScreen')
const importApps = () => import('@mobile/modules/dashboard/screens/AppsScreen')
const importDappBrowser = () => import('@mobile/modules/dashboard/screens/DappBrowserScreen')
const importWalletConnect = () => import('@mobile/modules/dashboard/screens/WalletConnectScreen')
const importShield = () => import('@mobile/modules/shield/screens/ShieldScreen')
const importUnshield = () => import('@mobile/modules/shield/screens/UnshieldScreen')
const importUnshieldToOrigin = () => import('@mobile/modules/shield/screens/UnshieldToOriginScreen')
const importTransfer = () => import('@mobile/modules/transfer/screens/TransferScreen')
const importSwapAndBridge = () =>
  import('@mobile/modules/swap-and-bridge/screens/SwapAndBridgeScreen')
const importAccountSelect = () =>
  import('@mobile/modules/account-select/screens/AccountSelectScreen')
const importPrivateTransfer = () => import('@mobile/modules/transfer/screens/PrivateTransferScreen')
const importSignAccountOp = () =>
  import('@mobile/modules/sign-account-op/screens/SignAccountOpScreen')
const importBenzin = () => import('@mobile/modules/action-requests/screens/BenzinScreen')
const importWebMainRoutes = () => import('@web/modules/router/components/MainRoutes')
const importSettingsRoutes = () =>
  import('@web/modules/settings/contexts/SettingsRoutesContext').then((m) => ({
    default: m.SettingsRoutesProvider
  }))

const ActivityScreen = lazy(importActivity)
const AppsScreen = lazy(importApps)
const DappBrowserScreen = lazy(importDappBrowser)
const WalletConnectScreen = lazy(importWalletConnect)
const ShieldScreen = lazy(importShield)
const UnshieldScreen = lazy(importUnshield)
const UnshieldToOriginScreen = lazy(importUnshieldToOrigin)
const TransferScreen = lazy(importTransfer)
const SwapAndBridgeScreen = lazy(importSwapAndBridge)
const AccountSelectScreen = lazy(importAccountSelect)
const PrivateTransferScreen = lazy(importPrivateTransfer)
const SignAccountOpScreen = lazy(importSignAccountOp)
const BenzinScreen = lazy(importBenzin)
const WebMainRoutes = lazy(importWebMainRoutes)
const SettingsRoutesProvider = lazy(importSettingsRoutes)

// Warm all route chunks once, after first paint. webpack dedupes against the
// chunk a lazy() may already be fetching, so this is safe to call eagerly.
const prefetchRouteChunks = () => {
  importActivity()
  importApps()
  importDappBrowser()
  importWalletConnect()
  importShield()
  importUnshield()
  importUnshieldToOrigin()
  importTransfer()
  importSwapAndBridge()
  importPrivateTransfer()
  importAccountSelect()
  importSignAccountOp()
  importBenzin()
  importWebMainRoutes()
  importSettingsRoutes()
}

// Suspense fallback shown while a lazy route chunk is fetching. Renders a
// centered spinner over an opaque page-color background so a cold chunk fetch
// (e.g. WebMainRoutes / onboarding) shows the loading state instead of a black
// flash. Background tracks the active theme (dark = #0D0D0F, light = white) so
// the fallback never flashes a dark slab over a light-mode page.
const RouteFallback = () => {
  const { theme } = useTheme()

  return (
    <View style={[styles.routeFallback, { backgroundColor: theme.primaryBackground }]}>
      <Spinner style={styles.spinner} />
    </View>
  )
}

// Routes the Home v2 shell owns on web; WebMainRoutes must not also render these.
const shellRoutes = [
  ROUTES.transactions,
  ROUTES.transactionsSettings,
  ROUTES.apps,
  ROUTES.dappBrowser,
  ROUTES.walletConnect,
  ROUTES.transfer,
  ROUTES.topUpGasTank,
  ROUTES.transferPrivate,
  ROUTES.swapAndBridge,
  ROUTES.swap,
  ROUTES.bridge,
  ROUTES.shield,
  ROUTES.unshield,
  ROUTES.unshieldToOrigin,
  ROUTES.accountSelect,
  ROUTES.signAccountOp,
  ROUTES.benzin
]

// Routes rendered eagerly (not code-split) — they paint on first commit without
// suspending. WebMainRoutes still mounts alongside them to own the *other*
// routes, but on these paths it has nothing visible to show, so its chunk should
// load silently (fallback={null}) rather than flashing a spinner over the
// already-rendered eager screen.
const eagerRoutes = [ROUTES.dashboard, ROUTES.keyStoreUnlock]

const Router = () => {
  const { path } = useRoute()
  const pathname = path?.substring(1)
  const { authStatus } = useAuth()
  const keystoreState = useController('KeystoreController').state
  const requestsState = useController('RequestsController').state
  const swapAndBridgeState = useController('SwapAndBridgeController').state
  const transferState = useController('TransferController').state
  const { areControllerStatesLoaded } = useContext(ControllersStateLoadedContext)
  // iOS home-screen PWA (standalone) top notch / Dynamic-Island inset. The Home
  // v2 shell screens apply this themselves via AppShell, but the @web onboarding
  // and settings screens (WebMainRoutes) don't, so their headers sit under the
  // status bar. Push that whole subtree down by the inset here. 0 in a normal
  // browser tab — no visible effect there.
  const insets = useSafeAreaInsets()
  const splashHidden = useRef(false)

  const isReady = authStatus !== AUTH_STATUS.LOADING && areControllerStatesLoaded

  // Send the user back to the deep link they followed before being detoured
  // through unlock / onboarding (stashed by KeystoreUnlockedRoute). Must be
  // above the `if (!isReady) return null` below — it is a hook.
  usePendingDeepLinkRestore(
    !keystoreState.isReadyToStoreKeys || !!keystoreState.isUnlocked,
    authStatus === AUTH_STATUS.AUTHENTICATED
  )

  useEffect(() => {
    if (isReady && !splashHidden.current) {
      splashHidden.current = true
      // Controllers are loaded and the first screen is about to mount: tear down
      // the inline boot loader (index.html). Any remaining gap (a cold route
      // chunk) is now covered by <Suspense fallback={<RouteFallback />}>, not a
      // black screen.
      dismissBootLoader()
      // Warm all lazy route chunks after first paint so sidebar/tab navigation
      // mounts synchronously instead of flashing through the Suspense fallback
      // on a cold chunk fetch.
      prefetchRouteChunks()
    }
  }, [isReady])

  if (!isReady) return null

  const initialRoute = getInitialRoute({
    keystoreState,
    authStatus,
    requestsState,
    swapAndBridgeState,
    transferState
  })

  // WebMainRoutes owns the visible screen only when the current path is neither a
  // shell route nor an eager route (i.e. onboarding / settings / get-started).
  // Only then does its lazy-chunk fetch need a visible spinner; otherwise it
  // loads behind the eager screen and a fullscreen spinner would just cover it.
  const webMainOwnsView =
    !shellRoutes.includes(pathname ?? '') && !eagerRoutes.includes(pathname ?? '')

  return (
    <View style={flexbox.flex1}>
      {initialRoute && !pathname && <Navigate to={initialRoute} replace />}
      {/* Eager + shell-lazy routes. Eager paths (unlock, dashboard) never
          suspend, so they paint immediately without a spinner. */}
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Dev-only visual QA of every transaction-status popup state, so the
              plan's QA matrix can be reviewed in both themes without
              broadcasting ten real transactions. Deliberately outside
              KeystoreUnlockedRoute/AuthenticatedRoute so it renders with no
              wallet; `isDev` keeps it out of any shipped build. */}
          {isDev && <Route path="dev-track-progress" element={<TrackProgressGalleryScreen />} />}
          <Route element={<KeystoreUnlockedRoute />}>
            <Route element={<AuthenticatedRoute />}>
              <Route path={ROUTES.dashboard} element={<DashboardScreen />} />
              {/* Activity + Apps get the Home v2 shell (sidebar / bottom-tabs) instead
                of the popup-style screens WebMainRoutes mounts for these routes.
                `shellRoutes` below suppresses the WebMainRoutes duplicate. */}
              <Route path={ROUTES.transactions} element={<ActivityScreen />} />
              <Route path={ROUTES.apps} element={<AppsScreen />} />
              {/* In-app dApp browser (iframe + postMessage provider bridge).
                Opened from the Apps screen for embeddable dApps. `shellRoutes`
                above suppresses the WebMainRoutes duplicate. */}
              <Route path={ROUTES.dappBrowser} element={<DappBrowserScreen />} />
              {/* Global WalletConnect — connect any dApp (new tab or anywhere) by
                pasting its wc: link. `shellRoutes` suppresses the WebMainRoutes
                duplicate. */}
              <Route path={ROUTES.walletConnect} element={<WalletConnectScreen />} />
              {/* Send gets the Home v2 shell (sidebar / bottom-tabs) + the
                desktop two-column layout instead of WebMainRoutes' popup form.
                `shellRoutes` above suppresses the WebMainRoutes duplicate. */}
              <Route path={ROUTES.transfer} element={<TransferScreen />} />
              {/* Top Up Gas Tank reuses the same TransferScreen (isTopUpScreen) so
                it gets the Home v2 shell + desktop two-column layout (Gas Tank
                balance + guidance) instead of WebMainRoutes' popup form. Must also
                be in `shellRoutes` above so the WebMainRoutes duplicate is suppressed. */}
              {/* GAS_TANK_ENABLED=false: keep the path owned by the shell but bounce
                to the dashboard, so a stale `#/top-up-gas-tank` link never lands on a
                blank screen (WebMainRoutes hides its copy of this route too). */}
              <Route
                path={ROUTES.topUpGasTank}
                element={
                  FEATURES.gasTankUiEnabled ? (
                    <TransferScreen isTopUpScreen />
                  ) : (
                    <Navigate to={`/${ROUTES.dashboard}`} replace />
                  )
                }
              />
              {/* Swap & Bridge gets the Home v2 shell + the desktop card layout
                (SwapAndBridgeScreen.web) instead of WebMainRoutes' popup form.
                `shellRoutes` above suppresses the WebMainRoutes duplicate.
                `key` = mode so Swap ⇄ Bridge remounts with a fresh form session
                instead of sharing the previous screen's token (Trello #506). */}
              <Route
                path={ROUTES.swap}
                element={<SwapAndBridgeScreen key="swap" forcedMode="swap" />}
              />
              <Route
                path={ROUTES.bridge}
                element={<SwapAndBridgeScreen key="bridge" forcedMode="bridge" />}
              />
              <Route
                path={ROUTES.swapAndBridge}
                element={<SwapAndBridgeScreen key="bridge" forcedMode="bridge" />}
              />
              {/* Private send (RAILGUN 0zk → 0zk). TransferScreen redirects here
                in PRIVATE mode; TokenDetails "Send" deep-links here directly. */}
              <Route path={ROUTES.transferPrivate} element={<PrivateTransferScreen />} />
              {/* Sign account op gets the Home v2 shell (sidebar/bottom-tab) +
                the responsive card layout (SignAccountOpScreen.web) instead of
                WebMainRoutes' notification-window form. `shellRoutes` above
                suppresses the WebMainRoutes duplicate. Needs the same controller
                provider WebMainRoutes wraps it with. */}
              <Route
                path={ROUTES.signAccountOp}
                element={
                  <SignAccountOpControllerStateProvider>
                    <SignAccountOpScreen />
                  </SignAccountOpControllerStateProvider>
                }
              />
              {/* Account switcher (balance-banner avatar → account-select). Mount the
                mobile-web popup-style screen here and suppress the WebMainRoutes
                duplicate via `shellRoutes` so the avatar opens the new popup card,
                not the old @web fullscreen screen. */}
              <Route path={ROUTES.accountSelect} element={<AccountSelectScreen />} />
              {/* Shield / Unshield get the Home v2 shell + the desktop card layout
                instead of WebMainRoutes' popup form. `shellRoutes` above
                suppresses the WebMainRoutes duplicates. */}
              <Route path={ROUTES.shield} element={<ShieldScreen />} />
              <Route path={ROUTES.unshield} element={<UnshieldScreen />} />
              {/* Transaction-status ("Transaction Progress") screen shown after a
                signed tx broadcasts. Mount the mobile-web popup-card variant here
                (@mobile BenzinScreen.web) instead of WebMainRoutes' fullscreen
                @web screen; `shellRoutes` above suppresses that duplicate. */}
              <Route path={ROUTES.benzin} element={<BenzinScreen />} />
              {/* Unshield to origin — recover a POI-blocked shield. Reached from the
                blocked Activity row; carries the original shield txid via nav state. */}
              <Route path={ROUTES.unshieldToOrigin} element={<UnshieldToOriginScreen />} />
              {/* Settings > Transaction history renders the Activity content inside
                the Settings panel (sidebar chrome) instead of the Home v2 shell. */}
              <Route element={<SettingsRoutesProvider />}>
                <Route path={ROUTES.transactionsSettings} element={<ActivityScreen embedded />} />
              </Route>
            </Route>
          </Route>
          {/* Unlock is the only eager route not wrapped by a guard. A fresh user
              with no keystore set up (no account yet) who deep-links to #/unlock
              would otherwise sit on a useless password screen. Redirect them to
              onboarding instead — once a keystore exists, show the unlock screen. */}
          <Route
            path={ROUTES.keyStoreUnlock}
            element={
              keystoreState.isReadyToStoreKeys ? (
                <KeyStoreUnlockScreen />
              ) : (
                <Navigate to={`/${ROUTES.getStarted}`} replace />
              )
            }
          />
          {/* Suppress "No routes matched" warnings — WebMainRoutes handles the rest. */}
          <Route path="*" element={null} />
        </Routes>
      </Suspense>
      {/* WebMainRoutes also defines transactions/apps; skip it on those paths so
          the shell screens above don't render stacked under the popup variants.
          Own Suspense boundary so its chunk fetch doesn't suspend (and blank) the
          eager unlock/dashboard screens above. Spinner only when it owns the view. */}
      {!shellRoutes.includes(pathname ?? '') && (
        // Only claim flex space (and add the standalone-PWA top inset) when
        // WebMainRoutes owns the visible screen. On eager routes (dashboard /
        // unlock) it mounts but renders nothing, so the wrapper must stay 0-height
        // or it would split the column with the eager screen. WebMainRoutes keeps a
        // single stable tree position here, so toggling the style never remounts it.
        <View style={webMainOwnsView ? [flexbox.flex1, { paddingTop: insets.top }] : undefined}>
          <Suspense fallback={webMainOwnsView ? <RouteFallback /> : null}>
            <WebMainRoutes />
          </Suspense>
        </View>
      )}
      {/* Persistent mobile bottom tab bar — mounted once here (outside <Routes>)
          so it survives tab navigations without remounting/flashing, and shows on
          Settings (rendered by WebMainRoutes) too. Self-gates by width + route. */}
      <MobileTabBar />
      {/* Off-chain message signing surfaced as a full-screen overlay (not a route)
          so the screen that requested the signature — and its hook watching for
          the result, e.g. gasless / permit swaps — stays mounted underneath.
          Self-gates on a pending sign-message request; renders nothing otherwise. */}
      <SignMessageOverlay />
      {/* On-chain `calls` signing (CCTP approve+depositForBurn) surfaced as a
          full-screen overlay on the swap route — same reason as above: the swap
          screen + its live CCTP hook (watching for the burn tx) must stay mounted
          underneath instead of being unmounted by a route change to /sign-account-op.
          Self-gates on a pending calls request on the swap route; null otherwise. */}
      <SignAccountOpOverlay />
      {/* WalletConnect connection-approval gate. A pasted wc: URI only starts
          pairing; this overlay requires an explicit approve before the wallet
          exposes the account to an external dApp. Self-gates on a pending
          proposal; null otherwise. */}
      <WalletConnectProposalOverlay />
    </View>
  )
}

const styles = StyleSheet.create({
  routeFallback: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center'
  },
  spinner: {
    width: 48,
    height: 48
  }
})

export default Router
